import axios, { type AxiosRequestConfig } from "axios";
import { handleAuthError, isAuthError } from "@/lib/auth/authErrorHandler";

/**
 * Options for the fetcher function
 */
export type FetcherOptions = {
  /** HTTP method (defaults to GET) */
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Request body data (for POST, PUT, PATCH) */
  data?: unknown;
  /** Additional headers */
  headers?: Record<string, string>;
};

// Generic fetch function with error handling using axios
export async function fetcher<T>(url: string, options?: FetcherOptions): Promise<T> {
  const axiosConfig: AxiosRequestConfig = {
    url,
    method: options?.method || "GET",
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  };

  // Add data if provided
  if (options?.data) {
    axiosConfig.data = options.data;
  }

  try {
    const response = await axios(axiosConfig);
    return response.data;
  } catch (error) {
    // Handle 401 authentication errors
    if (isAuthError(error)) {
      handleAuthError();
      // Throw error to prevent further processing while preserving original context
      const originalMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : String(error);
      throw new Error(`Authentication required: ${originalMessage}`);
    }

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || error.message;
      throw new Error(message);
    }
    throw error;
  }
}
